import React, { useCallback, useMemo } from 'react';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import { useGetDocumentsQuery, useGetUserQuery } from 'api/main';
import { INVESTOR_RELATED_DOCUMENTS } from 'utils/constants';
import { getDocumentJumioStatus, parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { DataTile, Loader, CancelButton } from 'components';
import CoSubscriberForm from 'containers/tunnel/steps/co-subscriber/CoSubscriberForm';
import ContactInfoPage from 'containers/tunnel/steps/contact-info/ContactInfoPage';
import Document from '../subscriptions/Document';
import { Box, Breadcrumbs, Grid, Link, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    documentsTile: {
        padding: '30px 60px',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        [theme.breakpoints.down('sm')]: {
            padding: '10px 30px',
        },
    },
    infoTile: {
        padding: '30px 60px',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        [theme.breakpoints.down('sm')]: {
            padding: '10px 30px',
        },
    },
}));

const InvestorAccountDetails = () => {
    const { userId } = useParams();
    const dispatch = useDispatch();
    const classes = useStyles();

    const { data: user, isLoading: userLoader, refetch } = useGetUserQuery({ id: userId });
    const profile = useMemo(() => {
        if (user) {
            return user?.subscriptions?.[0]?.role?.name?.includes('PP') ? 'PP' : 'PM';
        }
        return null;
    }, [user]);

    const { data: documents = [], refetch: refetchDocuments } = useGetDocumentsQuery({
        belongsTo: userId,
        pagination: false,
    });

    const investorRelatedDocs = useMemo(() => {
        let tempDocuments = [];
        INVESTOR_RELATED_DOCUMENTS.forEach(key => {
            const otherDocuments = documents.filter(doc => doc.type === key);
            tempDocuments = tempDocuments.concat(otherDocuments);
        });
        return tempDocuments;
    }, [documents]);

    const handleDocumentChange = useCallback(() => {
        refetchDocuments();
    }, [refetchDocuments]);

    const goBack = useCallback(() => {
        dispatch(push('/investors-list'));
    }, [dispatch]);

    return (
        <Loader loading={userLoader} marginTop={20}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Breadcrumbs separator={<icons.MenuRight />} aria-label="breadcrumb">
                    <Link
                        underline="hover"
                        variant="h5"
                        to="/investors-list"
                        component={RouterLink}
                    >
                        {texts.CLIENT_ACCOUNTS}
                    </Link>
                    <Link
                        underline="hover"
                        variant="h5"
                        to="/"
                        component={RouterLink}
                        onClick={e => {
                            e.preventDefault();
                            refetch();
                        }}
                    >
                        {profile === 'PP'
                            ? parseFullName(user)
                            : user?.investorCompany?.corporateName}
                    </Link>
                </Breadcrumbs>
            </Box>
            <DataTile title={texts.INFOS} className={classes.infoTile}>
                <Grid container spacing={5} justifyContent="space-around">
                    <Grid
                        item
                        sx={theme => ({
                            flex: '0 1 50%',
                            [theme.breakpoints.down('md')]: {
                                flex: '0 1 100%',
                            },
                        })}
                    >
                        {!!user?.subscriptions?.[0]?.coSubscriber && (
                            <Typography
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '18px', marginBottom: '20px' }}
                            >
                                {texts.SUBSCRIBER}
                            </Typography>
                        )}
                        <ContactInfoPage
                            isResubmit
                            hasStatusOk
                            isStepVerified
                            isPhoneVerified
                            isOnAdminSpace
                            specificSubscription={user?.subscriptions?.[0]}
                            specificUser={user}
                            refetch={refetch}
                        />
                    </Grid>
                    {!!user?.subscriptions?.[0]?.coSubscriber && (
                        <Grid
                            item
                            sx={theme => ({
                                flex: '0 1 50%',
                                height: '100%',
                                [theme.breakpoints.down('md')]: {
                                    flex: '0 1 100%',
                                },
                            })}
                        >
                            <Typography
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '18px', marginBottom: '20px' }}
                            >
                                {texts.CO_SUBSCRIBER}
                            </Typography>
                            <CoSubscriberForm
                                isOnAdminSpace
                                isResubmit
                                isStepVerified
                                isPhoneVerified
                                hasStatusOk
                                specificSubscription={user?.subscriptions?.[0]}
                                specificUser={user}
                                refetch={refetch}
                            />
                        </Grid>
                    )}
                </Grid>
                <Box alignSelf="center" marginTop={3}>
                    <CancelButton onClick={goBack}>{texts.CANCEL}</CancelButton>
                </Box>
            </DataTile>

            <DataTile
                title={texts.YOUR_INVESTOR_PROFILE_DOCUMENTS}
                className={classes.documentsTile}
            >
                {investorRelatedDocs.map(doc => (
                    <Box key={doc.id} display="flex" flexWrap="wrap" width="100%">
                        <Document
                            doc={doc}
                            isReplaceable
                            onChange={handleDocumentChange}
                            jumioStatus={getDocumentJumioStatus(
                                user?.subscriptions?.[0]?.userTunnelSteps,
                                doc.type
                            )}
                        />
                    </Box>
                ))}
                {!documents.length && (
                    <Typography align="center">{texts.NO_SUBSCRIPTION_DOCUMENTS}</Typography>
                )}
            </DataTile>
        </Loader>
    );
};

export default InvestorAccountDetails;
